import type { FeatureKey, Team } from '@boardflight/shared';
import { createContext, useContext, useMemo } from 'react';

/**
 * What this account can do, per team — the dashboard half of `@boardflight/shared`'s
 * `features.ts`.
 *
 * The list arrives on `GET /v1/teams`, which the dashboard already makes at boot, so knowing
 * whether a control should exist costs no request. It is a list of the keys that are **on**;
 * a key that is off is simply absent, and there is no plan in the payload to read. That is
 * deliberate and it is the whole product requirement: a free account's dashboard must be
 * indistinguishable from a build of BoardFlight where the paid feature does not exist.
 *
 * ## Hide, do not disable
 *
 * This is the one place that overrides the standing rule that a control the caller cannot use
 * stays visible and disabled with a tooltip saying why. That rule is about *permission* — "a
 * tester cannot publish, ask an admin" is useful, and hiding it just makes the screen a puzzle.
 * A feature the account does not have is not a permission problem, and a greyed-out button with
 * no explanation next to it is worse than no button: it advertises the thing while refusing to
 * say anything about it. So a missing feature removes its control outright.
 *
 * ## This is not the gate
 *
 * Every key here is enforced in the Worker as well (`packages/worker-core/src/features.ts`). Nothing on
 * this side is a security boundary — it decides what is worth drawing, and the API decides what
 * is allowed to happen. If the two ever disagree the API wins, which is the right way round.
 */

/**
 * Team id -> the keys that are on for the signed-in person in that team, or `null` for a team the
 * server did not tell us about. `null` and "an empty set" are different answers and must not be
 * allowed to collapse into each other — see `useFeature`.
 */
const FeaturesContext = createContext<ReadonlyMap<string, ReadonlySet<string> | null>>(new Map());

export function FeaturesProvider({
  teams,
  children,
}: {
  teams: readonly Team[];
  children: React.ReactNode;
}) {
  const value = useMemo(() => {
    const map = new Map<string, ReadonlySet<string> | null>();
    // `features` is optional on the wire. Absent means an API that was never asked, and the
    // honest reading of that is "not told" rather than "none" — `null`, which `useFeature` answers
    // as it answers a list still loading.
    for (const team of teams) {
      map.set(team.id, team.features ? new Set(team.features) : null);
    }
    return map;
  }, [teams]);

  return <FeaturesContext.Provider value={value}>{children}</FeaturesContext.Provider>;
}

/**
 * Whether `key` is on for the signed-in person in `teamId`.
 *
 * Before the list has landed — an `undefined` team, a page reached before a team is known, or a team
 * the server sent no list for — the answer is no. Which tier a feature is in lives in the database,
 * where an operator releases it (docs/plans-and-features.md), so this side cannot tell a free control
 * from one the person must never see; a control that appears a beat late is the price of never
 * drawing a paid or alpha one for a moment on every load. Once the list is in hand it is the only
 * answer.
 */
export function useFeature(teamId: string | undefined, key: FeatureKey): boolean {
  return featureIn(useContext(FeaturesContext), teamId, key);
}

/** `useFeature` without React, for the tests. */
export function featureIn(
  map: ReadonlyMap<string, ReadonlySet<string> | null>,
  teamId: string | undefined,
  key: FeatureKey,
): boolean {
  const set = teamId ? map.get(teamId) : null;
  return set ? set.has(key) : false;
}
